<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs" />

    <!-- Summary Stats Cards -->
    <div class="user-stats-row" v-if="summaryStats">
      <div class="user-stat-card stat-total">
        <div class="stat-icon-wrap"><vue-feather type="users" size="18" /></div>
        <div class="stat-info">
          <span class="stat-number">{{ summaryStats.totalUsers || 0 }}</span>
          <span class="stat-label">Total Users</span>
        </div>
      </div>
      <div class="user-stat-card stat-active">
        <div class="stat-icon-wrap"><vue-feather type="user-check" size="18" /></div>
        <div class="stat-info">
          <span class="stat-number">{{ summaryStats.activeUsers || 0 }}</span>
          <span class="stat-label">Active</span>
        </div>
      </div>
      <div class="user-stat-card stat-premium">
        <div class="stat-icon-wrap"><vue-feather type="award" size="18" /></div>
        <div class="stat-info">
          <span class="stat-number">{{ summaryStats.premiumUsers || 0 }}</span>
          <span class="stat-label">Premium</span>
        </div>
      </div>
      <div class="user-stat-card stat-new">
        <div class="stat-icon-wrap"><vue-feather type="user-plus" size="18" /></div>
        <div class="stat-info">
          <span class="stat-number">{{ summaryStats.newUsersThisMonth || 0 }}</span>
          <span class="stat-label">New (This Month)</span>
        </div>
      </div>
    </div>

    <!-- Search Section -->
    <div class="app-users-search-section">
      <div class="search-row">
        <div class="search-input-group">
          <select v-model="searchType" class="filter-select-modern search-type-select">
            <option value="name">Name</option>
            <option value="email">Email</option>
            <option value="uuid">UUID</option>
            <option value="mobile">Mobile Number</option>
          </select>
          <div class="search-input-wrapper">
            <vue-feather type="search" size="16" class="search-icon" />
            <input
              v-model="searchQuery"
              type="text"
              :placeholder="searchPlaceholder"
              class="search-input"
              @keyup.enter="handleSearch"
            />
            <button v-if="searchQuery" class="search-clear" @click="clearSearch">
              <vue-feather type="x" size="14" />
            </button>
          </div>
          <button class="btn btn-primary btn-search" @click="handleSearch">Search</button>
        </div>
        <select v-model="filterSubscription" class="filter-select-modern" @change="handleFilterChange">
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="basic">Basic</option>
          <option value="pro">Pro</option>
          <option value="unlimited">Unlimited</option>
        </select>
        <select v-model="filterStatus" class="filter-select-modern" @change="handleFilterChange">
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
        <select v-model="sortBy" class="filter-select-modern" @change="handleFilterChange">
          <option value="createdAt_desc">Newest First</option>
          <option value="createdAt_asc">Oldest First</option>
          <option value="lastLoginAt_desc">Recent Login</option>
          <option value="name_asc">Name A-Z</option>
        </select>
        <button v-if="isSuperAdmin" class="btn btn-outline-secondary btn-export" @click="exportUsers" :disabled="exporting">
          <vue-feather :type="exporting ? 'loader' : 'download'" size="14" :class="{ 'spin': exporting }" />
          Export
        </button>
      </div>
    </div>

    <DataTable
      :items="appUsers"
      :columns="columns"
      :loading="loading"
      :searchable="false"
      :paginated="false"
      empty-icon="users"
      empty-title="No app users found"
      empty-description="No users match your search criteria"
    >
      <template #cell-user="{ item }">
        <div class="table-avatar">
          <img v-if="item.avatar" :src="item.avatar" class="avatar-img" alt="" />
          <div v-else class="avatar-placeholder">{{ getInitials(item.fullName || item.name) }}</div>
          <div class="avatar-info">
            <span class="avatar-name">{{ item.fullName || item.name || 'N/A' }}</span>
            <span class="avatar-subtitle">{{ item.email }}</span>
          </div>
        </div>
      </template>

      <template #cell-mobile="{ item }">
        <span>{{ item.mobileNumber || item.mobile || 'N/A' }}</span>
      </template>

      <template #cell-uuid="{ item }">
        <code class="uuid-code" v-tooltip="'Click to copy'" @click="copyToClipboard(item.uuid || item.id)">
          {{ truncateUuid(item.uuid || item.id) }}
        </code>
      </template>

      <template #cell-status="{ item }">
        <span :class="['status-badge', item.isActive ? 'status-active' : 'status-inactive']">
          {{ item.isActive ? 'Active' : 'Inactive' }}
        </span>
      </template>

      <template #cell-lastLogin="{ item }">
        <span class="text-muted" v-if="item.lastLoginAt">{{ timeAgo(item.lastLoginAt) }}</span>
        <span class="text-muted faded" v-else>Never</span>
      </template>

      <template #cell-subscription="{ item }">
        <span :class="['subscription-badge', `subscription-${(item.subscriptionPlan || 'free').toLowerCase()}`]">
          {{ item.subscriptionPlan || 'Free' }}
        </span>
      </template>

      <template #cell-actions="{ item }">
        <div class="action-buttons">
          <button class="action-btn action-view" @click="viewUserDetails(item)" v-tooltip="'View details'">
            <vue-feather type="eye" size="14" />
          </button>
          <button 
            v-if="isSuperAdmin"
            class="action-btn action-toggle" 
            @click="toggleUserStatus(item)"
            v-tooltip="item.isActive ? 'Deactivate user' : 'Activate user'"
          >
            <vue-feather :type="item.isActive ? 'user-x' : 'user-check'" size="14" />
          </button>
          <button 
            v-if="isSuperAdmin"
            class="action-btn action-upgrade" 
            @click="openUpgradeModal(item)"
            v-tooltip="'Update subscription'"
          >
            <vue-feather type="award" size="14" />
          </button>
        </div>
      </template>
    </DataTable>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="pagination-wrapper">
      <div class="pagination-info">
        Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} users
      </div>
      <div class="pagination-controls">
        <button class="pagination-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)">
          <vue-feather type="chevron-left" size="16" />
        </button>
        <template v-for="page in visiblePages" :key="page">
          <button v-if="page !== '...'" class="pagination-btn" :class="{ active: page === currentPage }" @click="goToPage(page)">{{ page }}</button>
          <span v-else class="pagination-ellipsis">...</span>
        </template>
        <button class="pagination-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)">
          <vue-feather type="chevron-right" size="16" />
        </button>
      </div>
    </div>

    <!-- Enhanced User Details Modal -->
    <DetailViewModal
      v-model="showDetailsModal"
      title="App User Details"
      icon="user"
      size="lg"
      :loading="loadingDetails"
    >
      <div v-if="selectedUser" class="user-details-enhanced">
        <!-- User Header Card -->
        <div class="user-header-card">
          <div class="user-header-left">
            <div class="user-avatar-large">
              <img v-if="selectedUser.avatar" :src="selectedUser.avatar" alt="" />
              <span v-else>{{ getInitials(selectedUser.fullName || selectedUser.name) }}</span>
            </div>
            <div class="user-header-info">
              <h3>{{ selectedUser.fullName || selectedUser.name || 'N/A' }}</h3>
              <p>{{ selectedUser.email }}</p>
              <div class="user-header-badges">
                <span :class="['status-badge', selectedUser.isActive ? 'status-active' : 'status-inactive']">
                  {{ selectedUser.isActive ? 'Active' : 'Inactive' }}
                </span>
                <span :class="['subscription-badge', `subscription-${(selectedUser.subscriptionPlan || 'free').toLowerCase()}`]">
                  {{ selectedUser.subscriptionPlan || 'Free' }}
                </span>
                <span v-if="selectedUser.emailVerified" class="verified-badge">
                  <vue-feather type="check-circle" size="12" /> Verified
                </span>
              </div>
            </div>
          </div>
          <div class="user-header-right" v-if="isSuperAdmin">
            <button class="btn btn-sm btn-outline-primary" @click="showDetailsModal = false; openUpgradeModal(selectedUser)">
              <vue-feather type="award" size="14" /> Update Plan
            </button>
            <button class="btn btn-sm btn-outline-secondary" @click="activeDetailTab = 'examAccess'; fetchExamAccess(selectedUser)">
              <vue-feather type="lock" size="14" /> Exam Access
            </button>
          </div>
        </div>

        <!-- Detail Tabs -->
        <div class="detail-tabs">
          <button 
            v-for="tab in detailTabs" :key="tab.key"
            :class="['detail-tab', { active: activeDetailTab === tab.key }]"
            @click="switchDetailTab(tab.key)"
          >
            <vue-feather :type="tab.icon" size="14" />
            {{ tab.label }}
          </button>
        </div>

        <!-- Tab: Overview -->
        <div v-if="activeDetailTab === 'overview'" class="detail-tab-content">
          <div class="detail-grid-3">
            <div class="detail-card"><label>Full Name</label><span>{{ selectedUser.fullName || selectedUser.name || 'N/A' }}</span></div>
            <div class="detail-card"><label>Email</label><span>{{ selectedUser.email || 'N/A' }}</span></div>
            <div class="detail-card"><label>Mobile</label><span>{{ selectedUser.mobileNumber || selectedUser.mobile || 'N/A' }}</span></div>
            <div class="detail-card">
              <label>UUID</label>
              <code class="uuid-code-full" @click="copyToClipboard(selectedUser.uuid || selectedUser.id)" v-tooltip="'Click to copy'">{{ selectedUser.uuid || selectedUser.id }}</code>
            </div>
            <div class="detail-card"><label>Email Verified</label><span :class="selectedUser.emailVerified ? 'text-success' : 'text-danger'">{{ selectedUser.emailVerified ? 'Yes' : 'No' }}</span></div>
            <div class="detail-card"><label>Joined</label><span>{{ formatDate(selectedUser.createdAt) }}</span></div>
          </div>
          <div class="detail-section-title">Subscription</div>
          <div class="detail-grid-3">
            <div class="detail-card">
              <label>Current Plan</label>
              <span :class="['subscription-badge', `subscription-${(selectedUser.subscriptionPlan || 'free').toLowerCase()}`]">{{ selectedUser.subscriptionPlan || 'Free' }}</span>
            </div>
            <div class="detail-card"><label>Plan Expires</label><span>{{ formatDate(selectedUser.subscriptionExpiresAt) || 'N/A' }}</span></div>
            <div class="detail-card"><label>Last Login</label><span>{{ selectedUser.lastLoginAt ? timeAgo(selectedUser.lastLoginAt) : 'Never' }}</span></div>
          </div>
        </div>

        <!-- Tab: Usage & Activity -->
        <div v-if="activeDetailTab === 'usage'" class="detail-tab-content">
          <div v-if="loadingUsage" class="tab-loading"><div class="spinner-sm"></div> Loading usage data...</div>
          <template v-else>
            <div class="usage-stats-grid">
              <div class="usage-stat">
                <vue-feather type="edit-3" size="18" class="usage-icon text-primary" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.testsAttempted || 0 }}</span><span class="usage-label">Tests Attempted</span></div>
              </div>
              <div class="usage-stat">
                <vue-feather type="check-circle" size="18" class="usage-icon text-success" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.testsCompleted || 0 }}</span><span class="usage-label">Tests Completed</span></div>
              </div>
              <div class="usage-stat">
                <vue-feather type="percent" size="18" class="usage-icon text-warning" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.avgScore != null ? userUsage.avgScore + '%' : '-' }}</span><span class="usage-label">Avg Score</span></div>
              </div>
              <div class="usage-stat">
                <vue-feather type="clock" size="18" class="usage-icon text-info" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.totalStudyTimeMins ? formatStudyTime(userUsage.totalStudyTimeMins) : '-' }}</span><span class="usage-label">Study Time</span></div>
              </div>
              <div class="usage-stat">
                <vue-feather type="layers" size="18" class="usage-icon text-purple" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.flashcardsReviewed || 0 }}</span><span class="usage-label">Flashcards Reviewed</span></div>
              </div>
              <div class="usage-stat">
                <vue-feather type="calendar" size="18" class="usage-icon text-rose" />
                <div class="usage-stat-info"><span class="usage-value">{{ userUsage.loginCount || 0 }}</span><span class="usage-label">Total Logins</span></div>
              </div>
            </div>
            <div class="detail-section-title">Recent Activity</div>
            <div v-if="userUsage.recentActivity && userUsage.recentActivity.length" class="activity-timeline">
              <div v-for="(activity, idx) in userUsage.recentActivity" :key="idx" class="timeline-item">
                <div class="timeline-dot" :class="'dot-' + activity.type"></div>
                <div class="timeline-content">
                  <span class="timeline-text">{{ activity.description }}</span>
                  <span class="timeline-time">{{ timeAgo(activity.timestamp) }}</span>
                </div>
              </div>
            </div>
            <div v-else class="empty-state-sm">No recent activity</div>
          </template>
        </div>

        <!-- Tab: Exam Access (Locked Categories) -->
        <div v-if="activeDetailTab === 'examAccess'" class="detail-tab-content">
          <div v-if="loadingExamAccess" class="tab-loading"><div class="spinner-sm"></div> Loading exam access...</div>
          <template v-else>
            <div class="exam-access-header">
              <p class="exam-access-desc">Manage which exam categories this user can access. Locked categories require a subscription or manual unlock.</p>
            </div>
            <div v-if="examCategories.length" class="exam-categories-list">
              <div 
                v-for="cat in examCategories" :key="cat.category"
                :class="['exam-cat-item', { 'cat-locked': isExamCategoryLocked(cat.category) }]"
              >
                <div class="cat-info">
                  <vue-feather :type="isExamCategoryLocked(cat.category) ? 'lock' : 'unlock'" size="16" 
                    :class="isExamCategoryLocked(cat.category) ? 'text-danger' : 'text-success'" />
                  <div>
                    <span class="cat-name">{{ cat.category }}</span>
                    <span class="cat-count">{{ cat.examCount }} exam{{ cat.examCount !== 1 ? 's' : '' }}</span>
                  </div>
                </div>
                <button 
                  v-if="isSuperAdmin"
                  :class="['btn btn-sm', isExamCategoryLocked(cat.category) ? 'btn-outline-success' : 'btn-outline-danger']"
                  @click="toggleExamCategoryLock(cat.category)"
                  :disabled="savingExamAccess"
                >
                  {{ isExamCategoryLocked(cat.category) ? 'Unlock' : 'Lock' }}
                </button>
                <span v-else :class="['access-badge', isExamCategoryLocked(cat.category) ? 'badge-locked' : 'badge-unlocked']">
                  {{ isExamCategoryLocked(cat.category) ? 'Locked' : 'Unlocked' }}
                </span>
              </div>
            </div>
            <div v-else class="empty-state-sm">No exam categories found</div>
            <div v-if="isSuperAdmin && examCategories.length" class="exam-access-actions">
              <button class="btn btn-sm btn-outline-success" @click="unlockAllCategories" :disabled="savingExamAccess">
                <vue-feather type="unlock" size="12" /> Unlock All
              </button>
              <button class="btn btn-sm btn-outline-danger" @click="lockAllCategories" :disabled="savingExamAccess">
                <vue-feather type="lock" size="12" /> Lock All
              </button>
              <button class="btn btn-primary btn-sm" @click="saveExamAccess" :disabled="savingExamAccess || !examAccessDirty">
                <vue-feather v-if="savingExamAccess" type="loader" size="12" class="spin" />
                Save Changes
              </button>
            </div>
          </template>
        </div>

        <!-- Tab: Devices & Sessions -->
        <div v-if="activeDetailTab === 'devices'" class="detail-tab-content">
          <div v-if="loadingDevices" class="tab-loading"><div class="spinner-sm"></div> Loading device info...</div>
          <template v-else>
            <div v-if="userDevices.length" class="devices-list">
              <div v-for="(device, idx) in userDevices" :key="idx" class="device-item">
                <div class="device-icon-wrap">
                  <vue-feather :type="device.type === 'mobile' ? 'smartphone' : (device.type === 'tablet' ? 'tablet' : 'monitor')" size="18" />
                </div>
                <div class="device-info">
                  <span class="device-name">{{ device.deviceName || device.os || 'Unknown Device' }}</span>
                  <span class="device-meta">{{ device.browser }} · {{ device.os }} · {{ device.appVersion || '' }}</span>
                  <span class="device-last-seen">Last seen: {{ device.lastSeenAt ? timeAgo(device.lastSeenAt) : 'N/A' }}</span>
                </div>
                <span v-if="device.isCurrent" class="current-device-badge">Current</span>
              </div>
            </div>
            <div v-else class="empty-state-sm">No device information available</div>
          </template>
        </div>
      </div>
    </DetailViewModal>

    <!-- Upgrade Subscription Modal -->
    <CreateModal
      v-model="showUpgradeModal"
      title="Update Subscription"
      icon="award"
      size="md"
      :loading="upgradingSubscription"
      submit-text="Update Subscription"
      :submit-disabled="!upgradeForm.planId"
      @submit="upgradeSubscription"
    >
      <div v-if="upgradeUser" class="upgrade-form">
        <div class="upgrade-user-info">
          <div class="avatar-placeholder">{{ getInitials(upgradeUser.fullName || upgradeUser.name) }}</div>
          <div>
            <div class="upgrade-user-name">{{ upgradeUser.fullName || upgradeUser.name }}</div>
            <div class="upgrade-user-email">{{ upgradeUser.email }}</div>
          </div>
        </div>
        <div class="current-plan-info">
          <label>Current Plan</label>
          <span :class="['subscription-badge', `subscription-${(upgradeUser.subscriptionPlan || 'free').toLowerCase()}`]">{{ upgradeUser.subscriptionPlan || 'Free' }}</span>
          <span v-if="upgradeUser.subscriptionExpiresAt" class="plan-expiry">Expires: {{ formatDate(upgradeUser.subscriptionExpiresAt) }}</span>
        </div>
        <div class="form-group">
          <label class="form-label">New Subscription Plan <span class="text-danger">*</span></label>
          <div v-if="loadingPlans" class="loading-plans"><span class="spinner-sm"></span> Loading plans...</div>
          <div v-else-if="subscriptionPlans.length === 0" class="no-plans-message">No subscription plans available</div>
          <div v-else class="plans-grid">
            <div v-for="plan in subscriptionPlans" :key="plan.id"
              :class="['plan-card', { 'plan-selected': upgradeForm.planId === plan.id, 'plan-popular': plan.isPopular }]"
              @click="selectPlan(plan)">
              <div class="plan-header"><span class="plan-name">{{ plan.name }}</span><span v-if="plan.isPopular" class="plan-popular-badge">Popular</span></div>
              <div class="plan-price">{{ plan.formattedPrice || formatPrice(plan.priceCents, plan.currency) }}<span class="plan-cycle">/{{ plan.billingCycle }}</span></div>
              <div class="plan-tier">{{ plan.planTier }}</div>
              <div v-if="plan.featuresList && plan.featuresList.length" class="plan-features">
                <div v-for="(feature, idx) in plan.featuresList.slice(0, 3)" :key="idx" class="plan-feature"><vue-feather type="check" size="12" /> {{ feature }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Duration (Months)</label>
          <input type="text" class="form-control form-control-readonly" :value="durationLabel" readonly />
          <small class="text-muted">Duration is based on the selected plan's billing cycle</small>
        </div>
        <div class="form-group">
          <label class="form-label">Add Items (Optional)</label>
          <div v-if="loadingItems" class="loading-plans"><span class="spinner-sm"></span> Loading items...</div>
          <div v-else-if="purchasableItems.length === 0" class="no-plans-message">No purchasable items available</div>
          <div v-else class="items-list">
            <div v-for="item in purchasableItems" :key="item.id"
              :class="['item-card', { 'item-selected': upgradeForm.itemIds.includes(item.id) }]"
              @click="toggleItem(item)">
              <div class="item-checkbox">
                <vue-feather v-if="upgradeForm.itemIds.includes(item.id)" type="check-square" size="16" />
                <vue-feather v-else type="square" size="16" />
              </div>
              <div class="item-info"><span class="item-name">{{ item.name }}</span><span class="item-description">{{ item.description }}</span></div>
              <div class="item-price">{{ item.formattedPrice || formatPrice(item.priceCents, item.currency) }}</div>
            </div>
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Note (Optional)</label>
          <textarea v-model="upgradeForm.note" class="form-control" rows="2" placeholder="e.g., Complimentary upgrade for beta tester"></textarea>
        </div>
        <div v-if="selectedPlanDetails" class="upgrade-summary">
          <h5>Upgrade Summary</h5>
          <div class="summary-row"><span>Plan:</span><strong>{{ selectedPlanDetails.name }}</strong></div>
          <div class="summary-row"><span>Duration:</span><strong>{{ durationLabel }}</strong></div>
          <div v-if="selectedItems.length > 0" class="summary-row"><span>Items:</span><strong>{{ selectedItems.map(i => i.name).join(', ') }}</strong></div>
          <div class="summary-row"><span>New Expiry:</span><strong>{{ calculateNewExpiry() }}</strong></div>
        </div>
        <div v-if="upgradeError" class="alert alert-danger">{{ upgradeError }}</div>
      </div>
    </CreateModal>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import api from '@/services/api'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'
import DataTable from '@/components/data-table/DataTable.vue'
import DetailViewModal from '@/components/modals/DetailViewModal.vue'
import CreateModal from '@/components/modals/CreateModal.vue'

const API_BASE = '/v1/admin'

export default {
  name: 'AppUsersList',
  components: { DataTable, DetailViewModal, CreateModal },
  setup() {
    const authStore = useAuthStore()
    const { toast } = useNotifications()
    const isSuperAdmin = computed(() => authStore.isSuperAdmin)

    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Users', url: '/app/users/list' },
      { title: 'App Users', active: true }
    ]

    const columns = [
      { key: 'user', label: 'User', sortable: true, minWidth: '250px' },
      { key: 'mobile', label: 'Mobile', sortable: true },
      { key: 'uuid', label: 'UUID' },
      { key: 'subscription', label: 'Subscription', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'lastLogin', label: 'Last Login', sortable: true },
      { key: 'actions', label: 'Actions', class: 'text-end' }
    ]

    // List state
    const appUsers = ref([])
    const loading = ref(false)
    const searchQuery = ref('')
    const searchType = ref('name')
    const filterStatus = ref('')
    const filterSubscription = ref('')
    const sortBy = ref('createdAt_desc')
    const exporting = ref(false)

    // Summary stats
    const summaryStats = ref(null)

    const searchPlaceholder = computed(() => {
      const placeholders = { name: 'Enter user name...', email: 'Enter email address...', uuid: 'Enter user UUID...', mobile: 'Enter mobile number...' }
      return placeholders[searchType.value] || 'Search...'
    })

    // Pagination
    const currentPage = ref(1)
    const pageSize = ref(20)
    const totalCount = ref(0)
    const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value))

    // Details modal
    const showDetailsModal = ref(false)
    const selectedUser = ref(null)
    const loadingDetails = ref(false)
    const activeDetailTab = ref('overview')

    // Usage tab
    const loadingUsage = ref(false)
    const userUsage = ref({})

    // Exam access tab
    const loadingExamAccess = ref(false)
    const examCategories = ref([])
    const userLockedCategories = ref([])
    const originalLockedCategories = ref([])
    const savingExamAccess = ref(false)
    const examAccessDirty = computed(() => {
      return JSON.stringify([...userLockedCategories.value].sort()) !== JSON.stringify([...originalLockedCategories.value].sort())
    })

    // Devices tab
    const loadingDevices = ref(false)
    const userDevices = ref([])

    // Subscription plans
    const subscriptionPlans = ref([])
    const loadingPlans = ref(false)
    const purchasableItems = ref([])
    const loadingItems = ref(false)

    // Upgrade modal
    const showUpgradeModal = ref(false)
    const upgradeUser = ref(null)
    const upgradingSubscription = ref(false)
    const upgradeError = ref('')
    const upgradeForm = reactive({ planId: '', durationMonths: 1, note: '', itemIds: [] })

    const selectedPlanDetails = computed(() => {
      if (!upgradeForm.planId) return null
      return subscriptionPlans.value.find(p => p.id === upgradeForm.planId)
    })
    const selectedItems = computed(() => purchasableItems.value.filter(item => upgradeForm.itemIds.includes(item.id)))
    const durationLabel = computed(() => {
      if (!selectedPlanDetails.value) return '-'
      const cycle = selectedPlanDetails.value.billingCycle
      const labels = { monthly: '1 Month', quarterly: '3 Months', half_yearly: '6 Months', annual: '12 Months (1 Year)', yearly: '12 Months (1 Year)' }
      return labels[cycle] || cycle
    })

    const detailTabs = [
      { key: 'overview', label: 'Overview', icon: 'user' },
      { key: 'usage', label: 'Usage & Activity', icon: 'activity' },
      { key: 'examAccess', label: 'Exam Access', icon: 'lock' },
      { key: 'devices', label: 'Devices', icon: 'smartphone' }
    ]

    const visiblePages = computed(() => {
      const pages = []
      const total = totalPages.value
      const current = currentPage.value
      if (total <= 7) {
        for (let i = 1; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        if (current > 3) pages.push('...')
        const start = Math.max(2, current - 1)
        const end = Math.min(total - 1, current + 1)
        for (let i = start; i <= end; i++) pages.push(i)
        if (current < total - 2) pages.push('...')
        pages.push(total)
      }
      return pages
    })

    // ---- API Calls ----

    const fetchAppUsers = async () => {
      loading.value = true
      try {
        const [sortField, sortDir] = sortBy.value.split('_')
        const params = { page: currentPage.value, limit: pageSize.value, sortBy: sortField, sortDir }
        if (searchQuery.value) { params.searchType = searchType.value; params.search = searchQuery.value }
        if (filterStatus.value) params.status = filterStatus.value
        if (filterSubscription.value) params.subscription = filterSubscription.value

        const response = await api.get(`${API_BASE}/app-users`, { params })
        appUsers.value = response.data.users || response.data.data || []
        totalCount.value = response.data.total || response.data.totalCount || 0
      } catch (error) {
        console.error('Failed to fetch app users:', error)
        toast.error('Failed to load app users')
        appUsers.value = []
      } finally {
        loading.value = false
      }
    }

    const fetchSummaryStats = async () => {
      try {
        const response = await api.get(`${API_BASE}/app-users/summary`)
        summaryStats.value = response.data
      } catch (error) {
        console.warn('Could not fetch user summary stats:', error)
        summaryStats.value = { totalUsers: totalCount.value, activeUsers: 0, premiumUsers: 0, newUsersThisMonth: 0 }
      }
    }

    const fetchUserUsage = async (user) => {
      loadingUsage.value = true
      try {
        const userId = user.uuid || user.id
        const response = await api.get(`${API_BASE}/app-users/${userId}/usage`)
        userUsage.value = response.data
      } catch (error) {
        console.warn('Could not fetch user usage:', error)
        userUsage.value = {}
      } finally {
        loadingUsage.value = false
      }
    }

    const fetchExamAccess = async (user) => {
      loadingExamAccess.value = true
      try {
        const userId = user.uuid || user.id
        const [catRes, accessRes] = await Promise.all([
          api.get(`${API_BASE}/exam-categories`),
          api.get(`${API_BASE}/app-users/${userId}/exam-access`)
        ])
        examCategories.value = catRes.data.categories || catRes.data || []
        const locked = accessRes.data.lockedCategories || []
        userLockedCategories.value = [...locked]
        originalLockedCategories.value = [...locked]
      } catch (error) {
        console.warn('Could not fetch exam access:', error)
        examCategories.value = []
        userLockedCategories.value = []
        originalLockedCategories.value = []
      } finally {
        loadingExamAccess.value = false
      }
    }

    const fetchUserDevices = async (user) => {
      loadingDevices.value = true
      try {
        const userId = user.uuid || user.id
        const response = await api.get(`${API_BASE}/app-users/${userId}/devices`)
        userDevices.value = response.data.devices || response.data || []
      } catch (error) {
        console.warn('Could not fetch user devices:', error)
        userDevices.value = []
      } finally {
        loadingDevices.value = false
      }
    }

    // ---- Actions ----

    const handleSearch = () => { currentPage.value = 1; fetchAppUsers() }
    const handleFilterChange = () => { currentPage.value = 1; fetchAppUsers() }
    const clearSearch = () => { searchQuery.value = ''; currentPage.value = 1; fetchAppUsers() }
    const goToPage = (page) => { if (page >= 1 && page <= totalPages.value) { currentPage.value = page; fetchAppUsers() } }

    const viewUserDetails = async (user) => {
      selectedUser.value = user
      activeDetailTab.value = 'overview'
      // Reset all tab data so stale data from previous user doesn't persist
      userUsage.value = {}
      userDevices.value = []
      examCategories.value = []
      userLockedCategories.value = []
      originalLockedCategories.value = []
      showDetailsModal.value = true
      loadingDetails.value = true
      try {
        const response = await api.get(`${API_BASE}/app-users/${user.uuid || user.id}`)
        selectedUser.value = response.data
      } catch (error) {
        console.warn('Could not fetch user details:', error)
      } finally {
        loadingDetails.value = false
      }
    }

    const switchDetailTab = (tabKey) => {
      activeDetailTab.value = tabKey
      if (tabKey === 'usage' && Object.keys(userUsage.value).length === 0 && selectedUser.value) {
        fetchUserUsage(selectedUser.value)
      } else if (tabKey === 'examAccess' && examCategories.value.length === 0 && selectedUser.value) {
        fetchExamAccess(selectedUser.value)
      } else if (tabKey === 'devices' && userDevices.value.length === 0 && !loadingDevices.value && selectedUser.value) {
        fetchUserDevices(selectedUser.value)
      }
    }

    const toggleUserStatus = async (user) => {
      const newStatus = !user.isActive
      const action = newStatus ? 'activate' : 'deactivate'
      try {
        await api.put(`${API_BASE}/app-users/${user.uuid || user.id}/status`, { isActive: newStatus })
        const idx = appUsers.value.findIndex(u => (u.uuid || u.id) === (user.uuid || user.id))
        if (idx !== -1) appUsers.value[idx] = { ...appUsers.value[idx], isActive: newStatus }
        toast.success(`User ${action}d successfully`)
      } catch (error) {
        toast.error(`Failed to ${action} user`)
      }
    }

    const exportUsers = async () => {
      exporting.value = true
      try {
        const params = {}
        if (searchQuery.value) { params.searchType = searchType.value; params.search = searchQuery.value }
        if (filterStatus.value) params.status = filterStatus.value
        if (filterSubscription.value) params.subscription = filterSubscription.value
        const response = await api.get(`${API_BASE}/app-users/export`, { params, responseType: 'blob' })
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `app-users-${new Date().toISOString().slice(0, 10)}.csv`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success('Users exported successfully')
      } catch (error) {
        toast.error('Failed to export users')
      } finally {
        exporting.value = false
      }
    }

    // Exam access management
    const isExamCategoryLocked = (category) => userLockedCategories.value.includes(category)
    const toggleExamCategoryLock = (category) => {
      const idx = userLockedCategories.value.indexOf(category)
      if (idx === -1) { userLockedCategories.value.push(category) } else { userLockedCategories.value.splice(idx, 1) }
    }
    const unlockAllCategories = () => { userLockedCategories.value = [] }
    const lockAllCategories = () => { userLockedCategories.value = examCategories.value.map(c => c.category) }
    const saveExamAccess = async () => {
      if (!selectedUser.value) return
      savingExamAccess.value = true
      try {
        const userId = selectedUser.value.uuid || selectedUser.value.id
        await api.put(`${API_BASE}/app-users/${userId}/exam-access`, { lockedCategories: userLockedCategories.value })
        originalLockedCategories.value = [...userLockedCategories.value]
        toast.success('Exam access updated successfully')
      } catch (error) {
        toast.error('Failed to update exam access')
      } finally {
        savingExamAccess.value = false
      }
    }

    // Upgrade subscription
    const openUpgradeModal = async (user) => {
      upgradeUser.value = user
      upgradeForm.planId = ''; upgradeForm.durationMonths = 1; upgradeForm.note = ''; upgradeForm.itemIds = []
      upgradeError.value = ''
      showUpgradeModal.value = true
      const promises = []
      if (subscriptionPlans.value.length === 0) promises.push(fetchSubscriptionPlans())
      if (purchasableItems.value.length === 0) promises.push(fetchPurchasableItems())
      await Promise.all(promises)
    }
    const fetchSubscriptionPlans = async () => {
      loadingPlans.value = true
      try {
        const response = await api.get(`${API_BASE}/payment-admin/plans`)
        subscriptionPlans.value = (response.data.plans || []).filter(p => p.isActive)
      } catch (error) { toast.error('Failed to load subscription plans') } finally { loadingPlans.value = false }
    }
    const fetchPurchasableItems = async () => {
      loadingItems.value = true
      try {
        const response = await api.get(`${API_BASE}/payment-admin/items`)
        purchasableItems.value = (response.data.items || []).filter(i => i.isActive)
      } catch (error) { toast.error('Failed to load purchasable items') } finally { loadingItems.value = false }
    }
    const selectPlan = (plan) => {
      upgradeForm.planId = plan.id
      const cycleDurations = { monthly: 1, quarterly: 3, half_yearly: 6, annual: 12, yearly: 12 }
      upgradeForm.durationMonths = cycleDurations[plan.billingCycle] || 1
    }
    const toggleItem = (item) => {
      const idx = upgradeForm.itemIds.indexOf(item.id)
      if (idx === -1) { upgradeForm.itemIds.push(item.id) } else { upgradeForm.itemIds.splice(idx, 1) }
    }
    const upgradeSubscription = async () => {
      if (!upgradeForm.planId || !upgradeUser.value) return
      upgradingSubscription.value = true; upgradeError.value = ''
      try {
        const userId = upgradeUser.value.uuid || upgradeUser.value.id
        const payload = { planId: upgradeForm.planId, durationMonths: upgradeForm.durationMonths }
        if (upgradeForm.note) payload.note = upgradeForm.note
        if (upgradeForm.itemIds.length > 0) payload.itemIds = upgradeForm.itemIds
        const response = await api.post(`${API_BASE}/app-users/${userId}/upgrade-subscription`, payload)
        const index = appUsers.value.findIndex(u => (u.uuid || u.id) === userId)
        if (index !== -1) {
          const sub = response.data.subscription || {}
          appUsers.value[index] = { ...appUsers.value[index], subscriptionPlan: sub.planTier || sub.planName || selectedPlanDetails.value?.planTier, subscriptionExpiresAt: sub.currentPeriodEnd }
        }
        toast.success(`Subscription upgraded to ${selectedPlanDetails.value?.name || 'selected plan'}`)
        showUpgradeModal.value = false
      } catch (error) {
        upgradeError.value = error.response?.data?.error || error.response?.data?.message || 'Failed to upgrade subscription'
      } finally { upgradingSubscription.value = false }
    }

    // ---- Helpers ----
    const getInitials = (name) => { if (!name) return '??'; return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) }
    const truncateUuid = (uuid) => { if (!uuid) return 'N/A'; return uuid.length > 12 ? `${uuid.slice(0, 8)}...` : uuid }
    const copyToClipboard = async (text) => { try { await navigator.clipboard.writeText(text); toast.success('Copied to clipboard') } catch { toast.error('Failed to copy') } }
    const formatDate = (date) => { if (!date) return 'N/A'; return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }
    const formatPrice = (priceCents, currency = 'INR') => { const price = priceCents / 100; const symbols = { INR: '₹', USD: '$', EUR: '€' }; return `${symbols[currency] || currency}${price.toLocaleString()}` }
    const calculateNewExpiry = () => { const now = new Date(); now.setMonth(now.getMonth() + upgradeForm.durationMonths); return formatDate(now) }
    const formatStudyTime = (mins) => { if (mins < 60) return `${mins}m`; const h = Math.floor(mins / 60); const m = mins % 60; return m > 0 ? `${h}h ${m}m` : `${h}h` }
    const timeAgo = (date) => {
      if (!date) return 'N/A'
      const seconds = Math.floor((new Date() - new Date(date)) / 1000)
      if (seconds < 60) return 'Just now'
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      if (hours < 24) return `${hours}h ago`
      const days = Math.floor(hours / 24)
      if (days < 30) return `${days}d ago`
      return formatDate(date)
    }

    onMounted(() => { fetchAppUsers(); fetchSummaryStats() })

    return {
      breadcrumbs, columns, appUsers, loading, searchQuery, searchType, searchPlaceholder,
      filterStatus, filterSubscription, sortBy, exporting, summaryStats,
      currentPage, pageSize, totalCount, totalPages, visiblePages,
      showDetailsModal, selectedUser, loadingDetails, activeDetailTab, detailTabs,
      loadingUsage, userUsage, loadingExamAccess, examCategories, userLockedCategories,
      originalLockedCategories, savingExamAccess, examAccessDirty, loadingDevices, userDevices,
      isSuperAdmin, showUpgradeModal, upgradeUser, upgradingSubscription, upgradeError, upgradeForm,
      subscriptionPlans, loadingPlans, purchasableItems, loadingItems, selectedPlanDetails,
      selectedItems, durationLabel,
      fetchAppUsers, handleSearch, handleFilterChange, clearSearch, goToPage,
      viewUserDetails, switchDetailTab, toggleUserStatus, exportUsers,
      isExamCategoryLocked, toggleExamCategoryLock, unlockAllCategories, lockAllCategories,
      saveExamAccess, fetchExamAccess,
      openUpgradeModal, upgradeSubscription, fetchSubscriptionPlans, fetchPurchasableItems,
      selectPlan, toggleItem, formatPrice, calculateNewExpiry,
      getInitials, truncateUuid, copyToClipboard, formatDate, formatStudyTime, timeAgo
    }
  }
}
</script>

<style scoped>
/* Summary Stats Row */
.user-stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}
.user-stat-card {
  background: white;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.875rem;
  border: 1px solid #e5e7eb;
  transition: box-shadow 0.2s;
}
.user-stat-card:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
.stat-icon-wrap {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
}
.stat-total .stat-icon-wrap { background: #ede9fe; color: #7c3aed; }
.stat-active .stat-icon-wrap { background: #d1fae5; color: #059669; }
.stat-premium .stat-icon-wrap { background: #fef3c7; color: #b45309; }
.stat-new .stat-icon-wrap { background: #dbeafe; color: #2563eb; }
.stat-info { display: flex; flex-direction: column; }
.stat-number { font-size: 1.375rem; font-weight: 700; color: #111827; line-height: 1.2; }
.stat-label { font-size: 0.75rem; color: #6b7280; }

/* Search Section */
.app-users-search-section { background: white; border-radius: 12px 12px 0 0; padding: 1rem 1.5rem; border-bottom: 1px solid #e5e7eb; }
.search-row { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.search-input-group { display: flex; align-items: center; gap: 0.5rem; flex: 1; min-width: 300px; }
.search-type-select { width: auto; min-width: 130px; }
.search-input-wrapper { position: relative; flex: 1; }
.search-icon { position: absolute; left: 0.875rem; top: 50%; transform: translateY(-50%); color: #9ca3af; pointer-events: none; }
.search-input { width: 100%; padding: 0.625rem 2.5rem 0.625rem 2.5rem; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.875rem; transition: border-color 0.2s, box-shadow 0.2s; }
.search-input:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }
.search-clear { position: absolute; right: 0.5rem; top: 50%; transform: translateY(-50%); background: none; border: none; color: #9ca3af; cursor: pointer; padding: 0.25rem; display: flex; align-items: center; }
.search-clear:hover { color: #6b7280; }
.btn-search { padding: 0.625rem 1.25rem; white-space: nowrap; }
.btn-export { display: flex; align-items: center; gap: 0.375rem; padding: 0.625rem 1rem; white-space: nowrap; font-size: 0.875rem; }
.filter-select-modern { padding: 0.625rem 2rem 0.625rem 0.875rem; border: 1px solid #d1d5db; border-radius: 8px; font-size: 0.875rem; background: white url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e") no-repeat right 0.5rem center/1.25rem; appearance: none; cursor: pointer; }
.filter-select-modern:focus { outline: none; border-color: #6366f1; box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1); }

/* Badges */
.subscription-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; text-transform: capitalize; }
.subscription-free { background: #f3f4f6; color: #6b7280; }
.subscription-basic { background: #dbeafe; color: #1d4ed8; }
.subscription-pro { background: #fef3c7; color: #b45309; }
.subscription-unlimited { background: #ede9fe; color: #7c3aed; }
.status-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 9999px; font-size: 0.75rem; font-weight: 600; }
.status-active { background: #d1fae5; color: #065f46; }
.status-inactive { background: #fee2e2; color: #991b1b; }
.verified-badge { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.25rem 0.5rem; border-radius: 9999px; font-size: 0.7rem; font-weight: 600; background: #dbeafe; color: #1d4ed8; }
.faded { opacity: 0.5; }

/* Table */
.table-avatar { display: flex; align-items: center; gap: 0.75rem; }
.avatar-img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; }
.avatar-placeholder { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white; display: flex; align-items: center; justify-content: center; font-size: 0.75rem; font-weight: 700; flex-shrink: 0; }
.avatar-info { display: flex; flex-direction: column; }
.avatar-name { font-weight: 600; color: #111827; font-size: 0.875rem; }
.avatar-subtitle { font-size: 0.75rem; color: #6b7280; }
.uuid-code { font-family: 'SF Mono', monospace; font-size: 0.75rem; background: #f3f4f6; padding: 0.2rem 0.5rem; border-radius: 4px; cursor: pointer; color: #4b5563; }
.uuid-code:hover { background: #e5e7eb; }
.uuid-code-full { font-family: 'SF Mono', monospace; font-size: 0.75rem; background: #f3f4f6; padding: 0.2rem 0.5rem; border-radius: 4px; cursor: pointer; color: #4b5563; word-break: break-all; }
.action-buttons { display: flex; gap: 0.375rem; justify-content: flex-end; }
.action-btn { width: 32px; height: 32px; border-radius: 8px; border: 1px solid #e5e7eb; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; color: #6b7280; }
.action-btn:hover { border-color: #6366f1; color: #6366f1; background: #f5f3ff; }
.action-view:hover { border-color: #6366f1; color: #6366f1; }
.action-toggle:hover { border-color: #f59e0b; color: #f59e0b; background: #fffbeb; }
.action-upgrade:hover { border-color: #8b5cf6; color: #8b5cf6; background: #f5f3ff; }

/* Pagination */
.pagination-wrapper { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; background: white; border-radius: 0 0 12px 12px; border-top: 1px solid #e5e7eb; }
.pagination-info { font-size: 0.8125rem; color: #6b7280; }
.pagination-controls { display: flex; gap: 0.25rem; }
.pagination-btn { width: 36px; height: 36px; border-radius: 8px; border: 1px solid #e5e7eb; background: white; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 0.8125rem; font-weight: 500; color: #374151; transition: all 0.2s; }
.pagination-btn:hover:not(:disabled) { border-color: #6366f1; color: #6366f1; }
.pagination-btn.active { background: #6366f1; color: white; border-color: #6366f1; }
.pagination-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.pagination-ellipsis { display: flex; align-items: center; padding: 0 0.25rem; color: #9ca3af; }

/* Enhanced User Details Modal */
.user-details-enhanced { display: flex; flex-direction: column; gap: 1rem; }
.user-header-card { display: flex; justify-content: space-between; align-items: flex-start; padding: 1.25rem; background: linear-gradient(135deg, #f8fafc, #f1f5f9); border-radius: 12px; border: 1px solid #e2e8f0; }
.user-header-left { display: flex; gap: 1rem; align-items: center; }
.user-avatar-large { width: 56px; height: 56px; border-radius: 14px; background: linear-gradient(135deg, #6366f1, #8b5cf6); color: white; display: flex; align-items: center; justify-content: center; font-size: 1.125rem; font-weight: 700; flex-shrink: 0; overflow: hidden; }
.user-avatar-large img { width: 100%; height: 100%; object-fit: cover; }
.user-header-info h3 { margin: 0; font-size: 1.125rem; font-weight: 700; color: #111827; }
.user-header-info p { margin: 0.125rem 0 0.5rem; font-size: 0.8125rem; color: #6b7280; }
.user-header-badges { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.user-header-right { display: flex; gap: 0.5rem; flex-shrink: 0; }
.user-header-right .btn { display: flex; align-items: center; gap: 0.375rem; font-size: 0.75rem; }

/* Detail Tabs */
.detail-tabs { display: flex; gap: 0.25rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0; }
.detail-tab { display: flex; align-items: center; gap: 0.375rem; padding: 0.625rem 1rem; border: none; background: none; font-size: 0.8125rem; font-weight: 500; color: #6b7280; cursor: pointer; border-bottom: 2px solid transparent; transition: all 0.2s; }
.detail-tab:hover { color: #374151; }
.detail-tab.active { color: #6366f1; border-bottom-color: #6366f1; }
.detail-tab-content { padding-top: 1rem; }
.detail-section-title { font-size: 0.8125rem; font-weight: 600; color: #374151; margin: 1.25rem 0 0.75rem; padding-bottom: 0.375rem; border-bottom: 1px solid #f3f4f6; }
.detail-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
.detail-card { background: #f9fafb; border-radius: 8px; padding: 0.75rem; }
.detail-card label { display: block; font-size: 0.6875rem; font-weight: 600; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 0.25rem; }
.detail-card span { font-size: 0.875rem; color: #111827; font-weight: 500; }

/* Usage Stats */
.usage-stats-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.75rem; }
.usage-stat { display: flex; align-items: center; gap: 0.75rem; padding: 0.875rem; background: #f9fafb; border-radius: 10px; border: 1px solid #f3f4f6; }
.usage-icon { flex-shrink: 0; }
.usage-stat-info { display: flex; flex-direction: column; }
.usage-value { font-size: 1.125rem; font-weight: 700; color: #111827; }
.usage-label { font-size: 0.6875rem; color: #6b7280; }
.text-purple { color: #7c3aed; }
.text-rose { color: #e11d48; }

/* Activity Timeline */
.activity-timeline { display: flex; flex-direction: column; gap: 0; }
.timeline-item { display: flex; gap: 0.75rem; padding: 0.625rem 0; border-bottom: 1px solid #f3f4f6; }
.timeline-item:last-child { border-bottom: none; }
.timeline-dot { width: 8px; height: 8px; border-radius: 50%; margin-top: 0.375rem; flex-shrink: 0; }
.dot-test { background: #6366f1; }
.dot-login { background: #10b981; }
.dot-subscription { background: #f59e0b; }
.dot-flashcard { background: #8b5cf6; }
.dot-default { background: #9ca3af; }
.timeline-content { display: flex; flex-direction: column; }
.timeline-text { font-size: 0.8125rem; color: #374151; }
.timeline-time { font-size: 0.6875rem; color: #9ca3af; }

/* Exam Access */
.exam-access-header { margin-bottom: 0.75rem; }
.exam-access-desc { font-size: 0.8125rem; color: #6b7280; margin: 0; }
.exam-categories-list { display: flex; flex-direction: column; gap: 0.5rem; }
.exam-cat-item { display: flex; align-items: center; justify-content: space-between; padding: 0.75rem 1rem; background: #f9fafb; border-radius: 10px; border: 1px solid #e5e7eb; transition: all 0.2s; }
.exam-cat-item.cat-locked { background: #fef2f2; border-color: #fecaca; }
.cat-info { display: flex; align-items: center; gap: 0.75rem; }
.cat-name { font-weight: 600; font-size: 0.875rem; color: #111827; display: block; }
.cat-count { font-size: 0.6875rem; color: #9ca3af; }
.access-badge { font-size: 0.6875rem; font-weight: 600; padding: 0.25rem 0.5rem; border-radius: 6px; }
.badge-locked { background: #fee2e2; color: #991b1b; }
.badge-unlocked { background: #d1fae5; color: #065f46; }
.exam-access-actions { display: flex; gap: 0.5rem; margin-top: 1rem; justify-content: flex-end; }

/* Devices */
.devices-list { display: flex; flex-direction: column; gap: 0.5rem; }
.device-item { display: flex; align-items: center; gap: 0.875rem; padding: 0.875rem; background: #f9fafb; border-radius: 10px; border: 1px solid #f3f4f6; }
.device-icon-wrap { width: 40px; height: 40px; border-radius: 10px; background: #ede9fe; color: #7c3aed; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.device-info { display: flex; flex-direction: column; flex: 1; }
.device-name { font-weight: 600; font-size: 0.875rem; color: #111827; }
.device-meta { font-size: 0.75rem; color: #6b7280; }
.device-last-seen { font-size: 0.6875rem; color: #9ca3af; }
.current-device-badge { font-size: 0.6875rem; font-weight: 600; padding: 0.25rem 0.5rem; border-radius: 6px; background: #d1fae5; color: #065f46; }

/* Loading & Empty */
.tab-loading { display: flex; align-items: center; gap: 0.5rem; padding: 2rem; justify-content: center; color: #6b7280; font-size: 0.875rem; }
.empty-state-sm { text-align: center; padding: 2rem; color: #9ca3af; font-size: 0.875rem; }
.spinner-sm { width: 16px; height: 16px; border: 2px solid #e5e7eb; border-top-color: #6366f1; border-radius: 50%; animation: spin 0.6s linear infinite; display: inline-block; }
.spin { animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Upgrade Modal */
.upgrade-form { display: flex; flex-direction: column; gap: 1rem; }
.upgrade-user-info { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; background: #f9fafb; border-radius: 10px; }
.upgrade-user-name { font-weight: 600; color: #111827; }
.upgrade-user-email { font-size: 0.8125rem; color: #6b7280; }
.current-plan-info { display: flex; align-items: center; gap: 0.75rem; flex-wrap: wrap; }
.current-plan-info label { font-size: 0.8125rem; color: #6b7280; font-weight: 500; }
.plan-expiry { font-size: 0.75rem; color: #9ca3af; }
.plans-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 0.75rem; }
.plan-card { padding: 1rem; border: 2px solid #e5e7eb; border-radius: 10px; cursor: pointer; transition: all 0.2s; }
.plan-card:hover { border-color: #a5b4fc; }
.plan-selected { border-color: #6366f1; background: #f5f3ff; }
.plan-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.375rem; }
.plan-name { font-weight: 600; font-size: 0.875rem; color: #111827; }
.plan-popular-badge { font-size: 0.625rem; font-weight: 700; padding: 0.125rem 0.375rem; border-radius: 4px; background: #fef3c7; color: #b45309; }
.plan-price { font-size: 1.25rem; font-weight: 700; color: #111827; }
.plan-cycle { font-size: 0.75rem; font-weight: 400; color: #6b7280; }
.plan-tier { font-size: 0.6875rem; color: #6b7280; text-transform: capitalize; margin-bottom: 0.5rem; }
.plan-features { display: flex; flex-direction: column; gap: 0.25rem; }
.plan-feature { font-size: 0.6875rem; color: #059669; display: flex; align-items: center; gap: 0.25rem; }
.items-list { display: flex; flex-direction: column; gap: 0.5rem; }
.item-card { display: flex; align-items: center; gap: 0.75rem; padding: 0.75rem; border: 1px solid #e5e7eb; border-radius: 8px; cursor: pointer; transition: all 0.2s; }
.item-card:hover { border-color: #a5b4fc; }
.item-selected { border-color: #6366f1; background: #f5f3ff; }
.item-checkbox { color: #6366f1; flex-shrink: 0; }
.item-info { flex: 1; }
.item-name { font-weight: 600; font-size: 0.875rem; color: #111827; display: block; }
.item-description { font-size: 0.75rem; color: #6b7280; }
.item-price { font-weight: 600; color: #111827; font-size: 0.875rem; }
.upgrade-summary { background: #f9fafb; border-radius: 10px; padding: 1rem; }
.upgrade-summary h5 { font-size: 0.8125rem; font-weight: 600; color: #374151; margin: 0 0 0.5rem; }
.summary-row { display: flex; justify-content: space-between; padding: 0.25rem 0; font-size: 0.8125rem; }
.summary-row span { color: #6b7280; }
.loading-plans { display: flex; align-items: center; gap: 0.5rem; color: #6b7280; font-size: 0.875rem; padding: 1rem 0; }
.no-plans-message { color: #9ca3af; font-size: 0.875rem; padding: 1rem 0; }
.form-control-readonly { background: #f9fafb; cursor: not-allowed; }

/* Responsive */
@media (max-width: 768px) {
  .user-stats-row { grid-template-columns: repeat(2, 1fr); }
  .search-row { flex-direction: column; align-items: stretch; }
  .search-input-group { min-width: auto; }
  .detail-grid-3 { grid-template-columns: 1fr; }
  .usage-stats-grid { grid-template-columns: repeat(2, 1fr); }
  .user-header-card { flex-direction: column; gap: 1rem; }
  .user-header-right { width: 100%; }
  .detail-tabs { overflow-x: auto; }
}
</style>
